import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminService } from '../../services/admin.service';
import type { SurveyTypeListItemDto, SurveyVersionListItemDto } from '../../models/admin-dto';

@Component({
  selector: 'app-survey-types-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './survey-types-list.component.html',
  styleUrl: './survey-types-list.component.css'
})
export class SurveyTypesListComponent implements OnInit, OnDestroy {
  surveyTypes: SurveyTypeListItemDto[] = [];
  /** Versions per survey type (loaded after types). */
  versionsByTypeId: Record<number, SurveyVersionListItemDto[]> = {};
  loading = true;
  errorMessage: string | null = null;
  copyFeedback: string | null = null;
  /** Survey type id when versions modal is open; null when closed. */
  versionsModalTypeId: number | null = null;
  /** Copy feedback shown inside the versions modal. */
  modalCopyFeedback: string | null = null;
  /** Source survey type when clone modal is open; null when closed. */
  cloneModalSource: SurveyTypeListItemDto | null = null;
  cloneNewName = '';
  cloneNewDescription = '';
  cloneSubmitting = false;
  cloneError: string | null = null;
  private copyFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private modalCopyFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly admin: AdminService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.admin
      .getSurveyTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.surveyTypes = list ?? [];
          if (!this.surveyTypes.length) {
            this.loading = false;
            this.cdr.markForCheck();
            return;
          }
          forkJoin(this.surveyTypes.map((t) => this.admin.getSurveyVersionsByType(t.id)))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (versionArrays) => {
                this.versionsByTypeId = {};
                this.surveyTypes.forEach((t, i) => {
                  this.versionsByTypeId[t.id] = (versionArrays[i] ?? []) as SurveyVersionListItemDto[];
                });
                this.loading = false;
                this.cdr.markForCheck();
              },
              error: () => {
                this.loading = false;
                this.cdr.markForCheck();
              }
            });
        },
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Failed to load survey types.';
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.copyFeedbackTimer != null) clearTimeout(this.copyFeedbackTimer);
    if (this.modalCopyFeedbackTimer != null) clearTimeout(this.modalCopyFeedbackTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  openVersionsModal(typeId: number): void {
    this.versionsModalTypeId = typeId;
    this.modalCopyFeedback = null;
    this.cdr.markForCheck();
  }

  closeVersionsModal(): void {
    this.versionsModalTypeId = null;
    this.modalCopyFeedback = null;
    if (this.modalCopyFeedbackTimer != null) {
      clearTimeout(this.modalCopyFeedbackTimer);
      this.modalCopyFeedbackTimer = null;
    }
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.versionsModalTypeId !== null) this.closeVersionsModal();
    if (this.cloneModalSource !== null) this.closeCloneModal();
  }

  openCloneModal(surveyType: SurveyTypeListItemDto): void {
    this.cloneModalSource = surveyType;
    this.cloneNewName = `${surveyType.name} (Copy)`;
    this.cloneNewDescription = surveyType.description ?? '';
    this.cloneError = null;
    this.cdr.markForCheck();
  }

  closeCloneModal(): void {
    this.cloneModalSource = null;
    this.cloneNewName = '';
    this.cloneNewDescription = '';
    this.cloneError = null;
    this.cdr.markForCheck();
  }

  getCloneModalSourceName(): string {
    return this.cloneModalSource?.name ?? '';
  }

  submitClone(): void {
    if (this.cloneModalSource == null) return;
    const name = this.cloneNewName?.trim();
    if (!name) {
      this.cloneError = 'New survey name is required.';
      this.cdr.markForCheck();
      return;
    }
    this.cloneError = null;
    this.cloneSubmitting = true;
    this.cdr.markForCheck();
    const sourceId = this.cloneModalSource.id;
    this.admin
      .cloneSurveyType(sourceId, { newName: name, newDescription: this.cloneNewDescription?.trim() || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.closeCloneModal();
          this.cloneSubmitting = false;
          this.cdr.markForCheck();
          this.router.navigate(['/admin/survey-types', res.surveyTypeId, 'edit'], { queryParams: { versionId: res.surveyVersionId } });
        },
        error: (err: Error) => {
          this.cloneError = err?.message ?? 'Failed to create survey from existing.';
          this.cloneSubmitting = false;
          this.cdr.markForCheck();
        }
      });
  }

  getVersionsModalTypeName(): string {
    if (this.versionsModalTypeId == null) return '';
    const t = this.surveyTypes.find((x) => x.id === this.versionsModalTypeId);
    return t?.name ?? '';
  }

  copyVersionUrlInModal(versionId: number): void {
    const url = this.getPublishedSurveyUrl(versionId);
    if (this.modalCopyFeedbackTimer != null) clearTimeout(this.modalCopyFeedbackTimer);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => {
          this.modalCopyFeedback = 'Copied';
          this.cdr.markForCheck();
          this.modalCopyFeedbackTimer = setTimeout(() => {
            this.modalCopyFeedbackTimer = null;
            this.modalCopyFeedback = null;
            this.cdr.markForCheck();
          }, 2000);
        },
        () => {
          this.modalCopyFeedback = 'Failed to copy';
          this.cdr.markForCheck();
          this.modalCopyFeedbackTimer = setTimeout(() => {
            this.modalCopyFeedbackTimer = null;
            this.modalCopyFeedback = null;
            this.cdr.markForCheck();
          }, 2000);
        }
      );
    } else {
      this.modalCopyFeedback = 'Copy not supported';
      this.cdr.markForCheck();
    }
  }

  get isEmpty(): boolean {
    return !this.loading && this.surveyTypes.length === 0;
  }

  getVersions(typeId: number): SurveyVersionListItemDto[] {
    return this.versionsByTypeId[typeId] ?? [];
  }

  getPublishedSurveyUrl(versionId: number): string {
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    return `${origin}/intake/create?surveyVersionId=${versionId}`;
  }

  copyVersionUrl(versionId: number): void {
    const url = this.getPublishedSurveyUrl(versionId);
    if (this.copyFeedbackTimer != null) clearTimeout(this.copyFeedbackTimer);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => {
          this.copyFeedback = 'Copied';
          this.cdr.markForCheck();
          this.copyFeedbackTimer = setTimeout(() => {
            this.copyFeedbackTimer = null;
            this.copyFeedback = null;
            this.cdr.markForCheck();
          }, 2000);
        },
        () => {
          this.copyFeedback = 'Failed to copy';
          this.cdr.markForCheck();
          this.copyFeedbackTimer = setTimeout(() => {
            this.copyFeedbackTimer = null;
            this.copyFeedback = null;
            this.cdr.markForCheck();
          }, 2000);
        }
      );
    } else {
      this.copyFeedback = 'Copy not supported';
      this.cdr.markForCheck();
    }
  }
}
